import { createEffect, createSignal, Show, onCleanup } from "solid-js"
import type { Editor as EditorType } from '@tiptap/core'
import { createTiptapEditor } from "solid-tiptap"
import * as Y from 'yjs'

import CharacterCount from "@tiptap/extension-character-count"
import Collaboration from "@tiptap/extension-collaboration"
import CollaborationCursor from "@tiptap/extension-collaboration-cursor"
import Placeholder from '@tiptap/extension-placeholder'
import Highlight from "@tiptap/extension-highlight"
import TaskItem from "@tiptap/extension-task-item"
import TaskList from "@tiptap/extension-task-list"
import Underline from '@tiptap/extension-underline'
import Document from '@tiptap/extension-document'
import CodeBlock from '@tiptap/extension-code-block'
import BulletList from '@tiptap/extension-bullet-list'
import ListItem from '@tiptap/extension-list-item'
import Heading from '@tiptap/extension-heading'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Code from '@tiptap/extension-code'
import TextAlign from '@tiptap/extension-text-align'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import Dropcursor from '@tiptap/extension-dropcursor'
import Gapcursor from '@tiptap/extension-gapcursor'
import HardBreak from '@tiptap/extension-hard-break'
import Blockquote from '@tiptap/extension-blockquote'
import Image from '@tiptap/extension-image'
import Youtube from '@tiptap/extension-youtube'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Link from '@tiptap/extension-link'

import { HocuspocusProvider } from "@hocuspocus/provider"

import { useLocalStore } from '@/context/StateContext'

import { ToolbarContents } from './Toolbar'
import './index.css'
import { WebrtcProvider } from "y-webrtc"
import { IndexeddbPersistence } from "y-indexeddb"

export default function Editor(props: { id: string }) {
  const localStore = useLocalStore()

  const [editor, setEditor] = createSignal<EditorType | null>(null)
  const [containerRef, setContainerRef] = createSignal<HTMLDivElement | null>(null)
  const [provider, setProvider] = createSignal<WebrtcProvider | HocuspocusProvider | null>(null)
  const [persistence, setPersistence] = createSignal<IndexeddbPersistence | null>(null)
  const [document, setDocument] = createSignal<Y.Doc | null>(null)

  createEffect(async () => {
    const element = containerRef()

    if (!element || !props.id) return

    const doc = new Y.Doc()
    setDocument(doc)

    try {
      const persist = new IndexeddbPersistence(props.id, doc)
      setPersistence(persist)

      let webrtcProvider: WebrtcProvider | null = null
      let hocuspocusProvider: HocuspocusProvider | null = null

      const hocuspocusHost = "hocuspocus.trettin.org"
      const hocuspocusPort = 8443
      const hocuspocusUrl = `wss://${hocuspocusHost}:${hocuspocusPort}`

      const name = `${localStore.activeUserId}-${props.id}`

      try {
        hocuspocusProvider = new HocuspocusProvider({
          url: hocuspocusUrl,
          name,
          document: doc,
          onSynced: ({ state }) => {
            console.log("webedit sync", state)
          },
          onConnect: () => {
            // Don't set syncing here
          },
          onDisconnect: () => {
            // Don't set syncing here
          },
          onClose: () => {
            // Don't set syncing here
          },
          onMessage: ({ message }) => {
            // Only set syncing when receiving update messages
            // if (message.type === 'update' || message.type === 'sync') {
            //   setLocalStore("syncing", true)
            // }
          },
          onAwarenessChange: ({ states }) => {
            // setLocalStore("clients", states)
          }
        })
        setProvider(hocuspocusProvider)
      } catch (error) {
        console.error(error)
      }

      // webrtcProvider = new WebrtcProvider(props.id, doc, {
      //   signaling: ['wss://yjs.webedit.site'],

      //   // Specify an existing Awareness instance - see https://github.com/yjs/y-protocols
      //   // TODO: add global awareness provider
      //   // awareness: new awarenessProtocol.Awareness(doc),
      // })
      // setProvider(webrtcProvider)

      createTiptapEditor(() => ({
        enableContentCheck: true,
        onContentError: ({ disableCollaboration }) => {
          disableCollaboration()
        },
        onCreate: ({ editor: currentEditor }) => {
          setEditor(currentEditor)
        },
        element,
        extensions: [
          Document,
          Text,
          Paragraph,
          Heading.configure({
            levels: [1, 2, 3]
          }),
          Bold,
          Italic,
          Underline,
          Strike,
          Code,
          ListItem,
          OrderedList,
          BulletList,
          Superscript,
          Subscript,
          TaskList,
          TaskItem,
          Blockquote,
          CodeBlock,
          HorizontalRule,
          HardBreak,
          Highlight,
          Dropcursor,
          Gapcursor,
          Table.configure({
            resizable: true,
          }),
          TableRow,
          TableCell,
          TableHeader,
          TextAlign.configure({
            types: ['paragraph', 'heading'],
          }),
          Image.configure({
            inline: true,
          }),
          Youtube.configure({
          }),
          Collaboration.configure({
            document: doc,
          }),
          CollaborationCursor.extend().configure({
            provider: hocuspocusProvider,
          }),
          CharacterCount.extend().configure({
            limit: 10000,
          }),

          Placeholder.configure({
            placeholder: 'Start typing...',
          }),
          Link.configure({
            defaultProtocol: 'https',
            protocols: ['http', 'https'],
          }),
        ],
        editorProps: {
          attributes: {
            class: 'p-8 focus:outline-none prose max-w-full',
          },
        },
      }))
    } catch (e) {
      console.error(e)
    }
  })

  onCleanup(() => {
    const currentProvider = provider()
    const currentPersistence = persistence()
    const currentDocument = document()
    const currentEditor = editor()

    if (currentEditor) {
      currentEditor.destroy()
    }

    if (currentProvider) {
      currentProvider.disconnect()
      currentProvider.destroy()
    }

    if (currentPersistence) {
      currentPersistence.destroy()
    }

    if (currentDocument) {
      currentDocument.destroy()
    }
  })

  return (
    <div class="flex flex-col min-h-52 h-full overflow-y-auto p-6 gap-4 w-full text-gray-950 dark:text-gray-50 rounded-lg">
      <Show when={editor()} keyed>
        <ToolbarContents editor={editor()!} />
      </Show>
      <div
        class="h-full w-full rounded-b-lg"
        ref={setContainerRef}
      />
    </div>
  )
}
